import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@ai/ui-components/toast'
import { useConfiguratorApi } from '@/composables/api/useConfiguratorApi'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags'
import type { Action } from '@ai/ui-components'
import type { Configurator } from 'shared/src'
import type { Ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { LIST_ACTION_ICONS } from '@/components/BusinessLogic/TableActions'
import { useDeleteConfigurationsBulkMutation } from '@/composables/queries/configurations/useDeleteConfigurationsBulkMutation'
import { useDuplicateConfigurationsBulkMutation } from '@/composables/queries/configurations/useDuplicateConfigurationsBulkMutation'

export type ActionItem = Action & Required<Pick<Action, 'label' | 'icon' | 'danger'>>

interface UseConfiguratorActionsOptions {
  editTo: (id: string) => RouteLocationRaw
  duplicateTo: (id: string) => RouteLocationRaw
  exportConfiguration: (item: Configurator) => void
  deleteConfiguration: (id: string, element: HTMLElement) => Promise<void>
  listConfigsPaginated: Ref<Configurator[]>
  nativeWebComponent: HTMLElement
  onViewDetails?: (item: Configurator) => void
}

export function useConfiguratorActions(options: UseConfiguratorActionsOptions) {
  const { t } = useI18n({ useScope: 'global' })
  const toast = useToast()
  const { updateRoutingConfig } = useConfiguratorApi()
  const { showDevFeatures } = useFeatureFlags()

  // Delete modal state
  const selectedForDelete = ref<Configurator>()
  const openDeleteModal = ref(false)

  // Live test modal state
  const selectedForLiveTest = ref<Configurator>()
  const openLiveTestModal = ref(false)

  const toggleLiveTestModal = (item: Configurator) => {
    selectedForLiveTest.value = item
    openLiveTestModal.value = !openLiveTestModal.value
  }

  const toggleDeleteModal = (item: Configurator) => {
    selectedForDelete.value = item
    openDeleteModal.value = !openDeleteModal.value
  }

  // Unassign modal state
  const selectedForUnassign = ref<Configurator>()
  const openUnassignModal = ref(false)

  const toggleUnassignModal = (item: Configurator) => {
    selectedForUnassign.value = item
    openUnassignModal.value = !openUnassignModal.value
  }

  // Delete action
  const isDeletingId = ref<string | null>(null)
  const selectedKeys = ref<(string | number)[]>([])
  const { mutateAsync: bulkDeleteMutation } = useDeleteConfigurationsBulkMutation()
  const { mutateAsync: bulkDuplicateMutation } = useDuplicateConfigurationsBulkMutation()

  const openBulkDeleteModal = () => {
    selectedForDelete.value = undefined
    openDeleteModal.value = true
  }

  const deleteModalCount = computed(() => (selectedForDelete.value ? 0 : selectedKeys.value.length))

  const onBulkDeleteConfigurations = async (): Promise<boolean> => {
    const ids = selectedKeys.value.map(String)
    if (!ids.length) return false

    try {
      const result = await bulkDeleteMutation(ids)
      if (result.ok) {
        toast.success(t('configurations_deleted_successfully', { count: ids.length }))
        selectedKeys.value = []
        openDeleteModal.value = false
        return true
      }
      if (result.failedIds.length < ids.length) {
        toast.error(t('some_configurations_not_deleted'), { title: t('error_occurred') })
        selectedKeys.value = result.failedIds
        openDeleteModal.value = false
        return false
      }
      toast.error(t('error_occurred_try_again'), { title: t('error_occurred') })
      return false
    } catch {
      toast.error(t('error_occurred_try_again'), { title: t('error_occurred') })
      return false
    }
  }

  const onBulkDuplicateConfigurations = async (): Promise<boolean> => {
    const ids = selectedKeys.value.map(String)
    if (!ids.length) return false

    try {
      const result = await bulkDuplicateMutation(ids)
      if (result.ok) {
        toast.success(t('configurations_duplicated_successfully', { count: ids.length }))
        selectedKeys.value = []
        return true
      }
      if (result.failedIds.length < ids.length) {
        toast.error(t('some_configurations_not_duplicated'), { title: t('error_occurred') })
        selectedKeys.value = result.failedIds
        return false
      }
      toast.error(t('error_occurred_try_again'), { title: t('error_occurred') })
      return false
    } catch {
      toast.error(t('error_occurred_try_again'), { title: t('error_occurred') })
      return false
    }
  }

  const onConfirmDelete = async (): Promise<boolean> => {
    if (selectedForDelete.value) {
      return onDeleteConfiguration(selectedForDelete.value)
    }
    return onBulkDeleteConfigurations()
  }

  const onDeleteConfiguration = async (item: Configurator): Promise<boolean> => {
    if (!item.id || isDeletingId.value) return false

    isDeletingId.value = item.id

    const previous = [...options.listConfigsPaginated.value]
    options.listConfigsPaginated.value = options.listConfigsPaginated.value.filter(
      (c) => c.id !== item.id,
    )

    try {
      await options.deleteConfiguration(item.id, options.nativeWebComponent)
      openDeleteModal.value = false
      selectedKeys.value = selectedKeys.value.filter((k) => k !== item.id)
      toast.success(t('item_was_deleted', [item.name]))
      return true
    } catch (e) {
      console.error('Failed to delete configuration:', e)
      options.listConfigsPaginated.value = previous
      return false
    } finally {
      isDeletingId.value = null
    }
  }

  // Unassign action
  const onUnassignPhones = async (item: Configurator, phones: string[]): Promise<boolean> => {
    if (!phones.length) return false

    try {
      const results = await Promise.all(phones.map((phone) => updateRoutingConfig(phone, null, [])))
      const success = results.every((result) => result)
      if (!success) return false

      // Update item.phone: remove unassigned phones
      if (item.phone) {
        const remainingPhones = item.phone.split(', ').filter((p) => !phones.includes(p))
        item.phone = remainingPhones.length > 0 ? remainingPhones.join(', ') : null
      }

      openUnassignModal.value = false
      toast.success(t('item_was_unassigned', [item.name]))
      return true
    } catch (e) {
      console.error('Failed to unassign phone:', e)
      return false
    }
  }

  // Get actions for a row
  const getActions = (item: Configurator): ActionItem[] => {
    const hasPhone = !!item.phone

    const actions: ActionItem[] = [
      {
        label: t('view'),
        value: 'view_details',
        icon: LIST_ACTION_ICONS.preview,
        handler: () => {
          options.onViewDetails?.(item)
        },
        danger: false,
        dataTestid: 'action-view-details',
      },
      {
        label: t('edit'),
        value: 'edit',
        icon: LIST_ACTION_ICONS.edit,
        to: options.editTo(item.id),
        danger: false,
        dataTestid: 'action-edit',
      },
      {
        label: t('live_test'),
        value: 'live_test',
        icon: LIST_ACTION_ICONS.liveTest,
        handler: () => {
          toggleLiveTestModal(item)
        },
        danger: false,
        dataTestid: 'action-live-test',
      },
    ]

    if (showDevFeatures) {
      actions.push({
        label: t('export'),
        value: 'export',
        icon: LIST_ACTION_ICONS.export,
        handler: () => {
          options.exportConfiguration(item)
        },
        danger: false,
        dataTestid: 'action-export',
      })
    }

    actions.push({
      label: t('duplicate'),
      value: 'duplicate',
      icon: LIST_ACTION_ICONS.duplicate,
      to: options.duplicateTo(item.id),
      danger: false,
      dataTestid: 'action-duplicate',
    })

    if (hasPhone) {
      actions.push({
        label: t('unassign'),
        value: 'unassign',
        icon: LIST_ACTION_ICONS.unassignDanger,
        handler: () => {
          toggleUnassignModal(item)
        },
        danger: true,
        dataTestid: 'action-unassign',
      })
    }

    actions.push({
      label: t('delete'),
      value: 'delete',
      icon: LIST_ACTION_ICONS.deleteDanger,
      handler: () => {
        toggleDeleteModal(item)
      },
      danger: true,
      disabled: hasPhone,
      dataTestid: 'action-delete',
    })

    return actions
  }

  return {
    // Delete modal
    selectedForDelete,
    openDeleteModal,
    toggleDeleteModal,
    onDeleteConfiguration,

    // Unassign modal
    selectedForUnassign,
    openUnassignModal,
    toggleUnassignModal,
    onUnassignPhones,

    // Live test modal
    selectedForLiveTest,
    openLiveTestModal,
    toggleLiveTestModal,

    // Bulk delete
    selectedKeys,
    openBulkDeleteModal,
    deleteModalCount,
    onConfirmDelete,

    // Bulk duplicate
    onBulkDuplicateConfigurations,

    // Actions
    getActions,
  }
}
